import { toast } from 'sonner';
import { useAuthStore } from '../../stores/auth.store';
import * as cognitoService from '../auth/cognito.service';
import { config } from '../config';
/**
 * Centralized handler for an unrecoverable session-expired condition: clear
 * auth state, surface a toast so the user understands what happened, and
 * redirect to the login route. Uses a hard navigation to abort all in-flight
 * React/TanStack Query work cleanly.
 *
 * Idempotent: only fires once per page session.
 */
let sessionExpiredHandled = false;
function handleSessionExpired() {
    if (sessionExpiredHandled)
        return;
    sessionExpiredHandled = true;
    useAuthStore.getState().logout();
    toast.error('Session expired', {
        description: 'Please sign in again to continue.',
        duration: 6000,
    });
    // Hard redirect — cancels all pending fetches/queries and resets state.
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
}
/**
 * Custom fetch wrapper that:
 * 1. Proactively refreshes an expiring ID token (single-flight) before sending
 * 2. Adds Authorization + X-Tenant-Id headers
 * 3. Retries once on 401 by forcing a refresh (defense-in-depth)
 * 4. Cleans up and redirects to /login on unrecoverable refresh failure
 *
 * @param baseUrl - The API base URL (e.g., config.api.people)
 * @param endpoint - The endpoint path (e.g., '/employees')
 * @param options - Fetch options
 */
export async function authenticatedFetch(baseUrl, endpoint, options = {}) {
    const { user } = useAuthStore.getState();
    if (!user) {
        throw new Error('User not authenticated');
    }
    // Get a guaranteed-valid idToken (refreshes proactively if expiring).
    // If the refresh token itself is dead, this throws and we redirect.
    let idToken;
    try {
        idToken = await cognitoService.getValidIdToken();
    }
    catch (refreshError) {
        console.error('Proactive token refresh failed:', refreshError);
        handleSessionExpired();
        throw new Error('Session expired. Please log in again.');
    }
    // Build headers
    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${idToken}`);
    headers.set('X-Tenant-Id', user.tenantId);
    headers.set('Content-Type', 'application/json');
    // Build full URL: baseUrl + endpoint
    const url = `${baseUrl}${endpoint}`;
    let response = await fetch(url, {
        ...options,
        headers,
    });
    // 401 fallback: token rejected by the API even though we thought it was
    // valid (clock skew, key rotation, etc). Force a refresh and retry once.
    if (response.status === 401) {
        try {
            const result = await cognitoService.refreshSessionSingleFlight();
            useAuthStore.getState().setTokens(result.tokens);
            headers.set('Authorization', `Bearer ${result.tokens.idToken}`);
            response = await fetch(url, {
                ...options,
                headers,
            });
        }
        catch (refreshError) {
            console.error('Token refresh failed after 401:', refreshError);
            handleSessionExpired();
            throw new Error('Session expired. Please log in again.');
        }
    }
    return response;
}
/**
 * Create a typed API client for a specific service
 */
function createApiClient(baseUrl) {
    return {
        /**
         * GET request with authentication
         */
        async get(endpoint) {
            const response = await authenticatedFetch(baseUrl, endpoint, {
                method: 'GET',
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * POST request with authentication
         */
        async post(endpoint, data) {
            const options = {
                method: 'POST',
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            const response = await authenticatedFetch(baseUrl, endpoint, options);
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * PUT request with authentication
         */
        async put(endpoint, data) {
            const options = {
                method: 'PUT',
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            const response = await authenticatedFetch(baseUrl, endpoint, options);
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * PATCH request with authentication
         */
        async patch(endpoint, data) {
            const options = {
                method: 'PATCH',
            };
            if (data) {
                options.body = JSON.stringify(data);
            }
            const response = await authenticatedFetch(baseUrl, endpoint, options);
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            return response.json();
        },
        /**
         * DELETE request with authentication
         */
        async delete(endpoint) {
            const response = await authenticatedFetch(baseUrl, endpoint, {
                method: 'DELETE',
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Request failed');
            }
            // Handle 204 No Content
            if (response.status === 204) {
                return {};
            }
            return response.json();
        },
    };
}
/**
 * Service-specific API clients using config.api.* endpoints
 */
export const apiClient = createApiClient(config.api.people);
export const identityApiClient = createApiClient(config.api.identity);
export const timeApiClient = createApiClient(config.api.time);
export const leaveApiClient = createApiClient(config.api.leave);
export const payrollApiClient = createApiClient(config.api.payroll);
export const scheduleApiClient = createApiClient(config.api.schedule);
export const notificationsApiClient = createApiClient(config.api.notifications);
